<template>
  <div class="product-shoppingcartbutton" :class="{'small' : small}">
    <b-button class="shoppingcartbutton" variant="custom" :disabled="isDisabled" @click.stop="addToCart">
      <div class="shoppingcartbutton-quantity" @click.stop>
        <b-dropdown
          v-if="!quantity.customMode"
          :text="dropdownText"
          size="sm"
          :dropup="dropup"
          no-flip
        >
          <b-dropdown-item
            v-for="item in dropdownItems"
            :key="item"
            @click="setQuantity(item)"
          >{{ item }}</b-dropdown-item>
        </b-dropdown>

        <b-input
          v-if="quantity.customMode"
          ref="quantityControl"
          v-model="quantity.value"
          @blur="toggleQuantityCustomMode(false)"
        />
      </div>

      <div class="shoppingcartbutton-text"><slot/></div>

      <div class="shoppingcartbutton-quantity invisible" v-if="!small" aria-hidden="true">
        <b-dropdown v-if="!quantity.customMode" :text="dropdownText" disabled tabindex="-1"></b-dropdown>
        <b-input v-if="quantity.customMode" v-model="quantity.value" disabled tabindex="-1"/>
      </div>
    </b-button>
    <div v-if="showVariantSelectionHint" class="text-warning mt-1">
      <lazy-icon icon="b-exclamation-circle" class="mr-1" />{{ variantSelectionHint }}
    </div>
  </div>
</template>

<script>
import { COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS, COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS } from '@/constants'

import { closest } from '@/assets/js/helper/array'

export default {
  name: 'ProductShoppingcartButton',
  props: {
    productId: {
      type: String,
      required: true
    },
    productName: {
      type: String,
      default: ''
    },
    quantityinfo: {
      type: Object,
      default: () => ({})
    },
    showCustomQuantity: {
      type: Boolean,
      default: true
    },
    small: {
      type: Boolean,
      default: false
    },
    dropup: {
      type: Boolean,
      default: true
    },
    gtmList: {
      type: String,
      default: 'Warenkorb'
    },
    reloadFullBasketOnAdd: {
      type: Boolean,
      default: false
    },
    enableAccessoryBundle: {
      type: Boolean,
      default: false
    },
    requireResolvedVariantSelection: {
      type: Boolean,
      default: false
    },
    variantSelectionHint: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      quantity: {
        customMode: false,
        value: null,
        tempValue: null
      },
      accessoryBundle: {
        mainProductId: null,
        selectedItems: []
      },
      requiresVariantSelection: false
    }
  },
  computed: {
    quantityOptions () {
      // must be >= 1, fallback = 1
      const max = Math.trunc(Math.max(1, Number(this.quantityinfo.max || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.max)))
      // must be >= 1 and <= max, fallback = 1 or max
      const min = Math.trunc(Math.max(1, Math.min(max, Number(this.quantityinfo.min || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.min))))
      // must be >= 1, fallback = 1
      const step = Math.trunc(Math.max(1, Number(this.quantityinfo.step || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.step)))

      return {
        min,
        max,
        range: max - min,
        step
      }
    },
    quantitySteps () {
      // add 1 for initial step (e.g. quantityOptions.min)
      const stepCount = Math.trunc(this.quantityOptions.range / this.quantityOptions.step) + 1

      return Array
        .from(Array(stepCount).keys())
        .map(step => step === 0 ? this.quantityOptions.min : this.quantityOptions.min + step * this.quantityOptions.step)
    },
    quantityInit () {
      // must by one >= 1 and <= quantitySteps.length, fallback = 0 or quantitySteps.length - 1
      const initialStep = Number(Math.min(this.quantitySteps.length, Math.max(1, this.quantityinfo.initialStep || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.initialStep))) - 1
      return this.quantitySteps[initialStep]
    },
    dropdownText () {
      return (this.quantity.value || '').toString()
    },
    dropdownItems () {
      let result = this.quantitySteps
        .slice(0, COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS)
      if (this.showCustomQuantity) {
        result = result.concat(this.quantitySteps.length > COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS ? this.$t(`${this.tPath}.customquantitylabel`) : [])
      }
      return result
    },
    isDisabled() {
      return this.requireResolvedVariantSelection && this.requiresVariantSelection
    },
    showVariantSelectionHint() {
      return this.isDisabled && this.variantSelectionHint.length > 0
    }
  },
  methods: {
    handleVariantSelectionRequired (isRequired = false) {
      this.requiresVariantSelection = Boolean(isRequired)
    },
    localizedValue (value) {
      if (value == null) {
        return ''
      }

      if (typeof value === 'string') {
        return value
      }

      const locale = this.$i18n.locale
      return value[locale] || value[locale.split('-')[0]] || Object.values(value)[0] || ''
    },
    getItemDisplayName (item = {}) {
      return this.localizedValue(item.title1) || this.localizedValue(item.name) || ''
    },
    onAccessorySelectionChanged(payload = {}) {
      if (!this.enableAccessoryBundle || payload.mainProductId !== this.productId) {
        this.accessoryBundle = {
          mainProductId: null,
          selectedItems: []
        }
        return
      }

      this.accessoryBundle = {
        mainProductId: payload.mainProductId,
        selectedItems: payload.selectedItems || []
      }
    },
    setQuantity (value = this.quantityOptions.min) {
      if (typeof value === 'number') {
        this.quantity.value = value
      } else {
        this.toggleQuantityCustomMode(true)
      }
    },
    toggleQuantityCustomMode (state = false) {
      if (state) {
        this.quantity.tempValue = this.quantity.value
        this.quantity.value = null
        this.quantity.customMode = true

        this.$nextTick(() => {
          this.$refs.quantityControl.focus()
        })
      } else {
        const quantityValue = Number(this.quantity.value)
        const isNonNumeric = !/^-?([0-9.]){1,}$/.test(this.quantity.value)
        const isOutOfRange = quantityValue < this.quantityOptions.min || quantityValue > this.quantityOptions.max
        const isOutOfSteps = !this.quantitySteps.includes(quantityValue)

        if (isNonNumeric || isOutOfRange) {
          this.quantity.value = this.quantity.tempValue
        } else if (isOutOfSteps) {
          this.quantity.value = closest(this.quantitySteps, quantityValue)
        }

        this.quantity.tempValue = null
        this.quantity.customMode = false
        this.$emit('input', Number(this.quantity.value))
      }
    },
    async addToCart () {
      if (this.isDisabled) {
        return
      }

      const q = Math.max(1, Number(this.quantity.value) || this.quantityOptions?.min || 1)

      if (!this.enableAccessoryBundle || this.accessoryBundle.mainProductId !== this.productId) {
        await this.$store.dispatch('shoppingcart/addItem', { productId: this.productId, quantity: q, gtmList: this.gtmList, reloadFullBasket: this.reloadFullBasketOnAdd })
        return
      }

      const items = [{
        productId: this.productId,
        quantity: q,
        gtmList: this.gtmList,
        displayName: this.productName
      }]

      for (const accessoryItem of this.accessoryBundle.selectedItems) {
        if (!accessoryItem?.productId || Number(accessoryItem.quantity) <= 0) {
          continue
        }

        const accessoryQuantity = Math.max(1, Number(accessoryItem.quantity) || 1)

        items.push({
          productId: accessoryItem.productId,
          quantity: accessoryQuantity * q,
          gtmList: this.gtmList,
          displayName: this.getItemDisplayName(accessoryItem)
        })
      }

      await this.$store.dispatch('shoppingcart/addItems', {
        items,
        gtmList: this.gtmList,
        reloadFullBasket: this.reloadFullBasketOnAdd
      })
    }
  },
  created () {
    this.quantity.value = this.quantityInit
    this.$root.$on('product-accessories:changed', this.onAccessorySelectionChanged)
    if (this.requireResolvedVariantSelection) {
      this.$root.$on('product:variant-selection-required', this.handleVariantSelectionRequired)
    }
  },
  beforeDestroy() {
    this.$root.$off('product-accessories:changed', this.onAccessorySelectionChanged)
    if (this.requireResolvedVariantSelection) {
      this.$root.$off('product:variant-selection-required', this.handleVariantSelectionRequired)
    }
  }
}
</script>

<style lang="scss">
$productshoppingcartbutton-padding-y: $btn-padding-y * 0.4 !default;
$productshoppingcartbutton-padding-x: $productshoppingcartbutton-padding-y !default;
$productshoppingcartbutton-bg: $primary !default;
$productshoppingcartbutton-border: transparent !default;
$productshoppingcartbutton-font-weight: $font-weight-bold !default;
$productshoppingcartbutton-line-height: 1.1 !default;
$productshoppingcartbutton-hover-bg: darken($productshoppingcartbutton-bg, 5%) !default;
$productshoppingcartbutton-hover-border: darken($productshoppingcartbutton-border, 5%) !default;

$productshoppingcartbutton-quantity-width: $spacer * 4 !default;
$productshoppingcartbutton-quantity-height: $input-height-sm !default;

$productshoppingcartbutton-quantity-toggle-padding-y: 0 !default;
$productshoppingcartbutton-quantity-toggle-padding-x: $btn-padding-x * 0.5 !default;
$productshoppingcartbutton-quantity-toggle-bg: darken($productshoppingcartbutton-bg, 2.5%) !default;
$productshoppingcartbutton-quantity-toggle-border: darken($productshoppingcartbutton-border, 2.5%) !default;
$productshoppingcartbutton-quantity-toggle-hover-bg: darken($productshoppingcartbutton-quantity-toggle-bg, 6%) !default;
$productshoppingcartbutton-quantity-toggle-hover-border: darken($productshoppingcartbutton-quantity-toggle-border, 6%) !default;

$productshoppingcartbutton-quantity-menu-bg: $productshoppingcartbutton-bg !default;
$productshoppingcartbutton-quantity-menu-border: $productshoppingcartbutton-border !default;
$productshoppingcartbutton-quantity-menu-caret-size: $spacer * 0.6 !default;
$productshoppingcartbutton-quantity-menu-item-padding-y: $dropdown-item-padding-y !default;
$productshoppingcartbutton-quantity-menu-item-padding-x: $dropdown-item-padding-x * 0.6 !default;
$productshoppingcartbutton-quantity-menu-item-hover-bg: $productshoppingcartbutton-hover-bg !default;

$productshoppingcartbutton-quantity-control-padding-y: $productshoppingcartbutton-quantity-toggle-padding-y !default;
$productshoppingcartbutton-quantity-control-padding-x: $productshoppingcartbutton-quantity-toggle-padding-x !default;
$productshoppingcartbutton-quantity-control-bg: $productshoppingcartbutton-quantity-toggle-bg !default;
$productshoppingcartbutton-quantity-control-border: $productshoppingcartbutton-quantity-toggle-bg !default;
$productshoppingcartbutton-quantity-control-hover-bg: $productshoppingcartbutton-quantity-toggle-hover-bg !default;
$productshoppingcartbutton-quantity-control-hover-border: $productshoppingcartbutton-quantity-toggle-hover-border !default;
$productshoppingcartbutton-quantity-control-focus-bg: $productshoppingcartbutton-quantity-toggle-hover-bg !default;
$productshoppingcartbutton-quantity-control-focus-border: $white !default;

$productshoppingcartbutton-text-padding-y: 0 !default;
$productshoppingcartbutton-text-padding-x: $spacer !default;

.product-shoppingcartbutton {
  .shoppingcartbutton {
    @include button-variant($productshoppingcartbutton-bg, $productshoppingcartbutton-border, $productshoppingcartbutton-hover-bg, $productshoppingcartbutton-hover-border);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: $productshoppingcartbutton-padding-y $productshoppingcartbutton-padding-x;
    width: 100%;
    font-weight: $productshoppingcartbutton-font-weight;
    line-height: $productshoppingcartbutton-line-height;

    .shoppingcartbutton-quantity {
      width: $productshoppingcartbutton-quantity-width;
      height: $productshoppingcartbutton-quantity-height;

      .dropdown {
        width: 100%;
        height: 100%;

        .dropdown-toggle {
          @include button-variant($productshoppingcartbutton-quantity-toggle-bg, $productshoppingcartbutton-quantity-toggle-border, $productshoppingcartbutton-quantity-toggle-hover-bg, $productshoppingcartbutton-quantity-toggle-hover-border);
          padding-top: $productshoppingcartbutton-quantity-toggle-padding-y;
          padding-bottom: $productshoppingcartbutton-quantity-toggle-padding-y;
          padding-left: $productshoppingcartbutton-quantity-toggle-padding-x;
          height: 100%;
          background-image: escape-svg(str-replace($dropdown-toggle-icon, "fill='#{$dropdown-toggle-color}'", "fill='#{color-yiq($productshoppingcartbutton-quantity-toggle-bg)}'"));
          box-shadow: none!important;
        }

        .dropdown-menu {
          margin-bottom: 0;
          margin-left: 50%;
          min-width: 0;
          background-color: $productshoppingcartbutton-quantity-menu-bg;
          border-color: $productshoppingcartbutton-quantity-menu-border;
          color: color-yiq($productshoppingcartbutton-quantity-menu-bg);

          .dropdown-item {
            padding: $productshoppingcartbutton-quantity-menu-item-padding-y $productshoppingcartbutton-quantity-menu-item-padding-x;
            color: inherit;

            &:hover {
              background-color: $productshoppingcartbutton-quantity-menu-item-hover-bg;
            }
          }
        }

        &.dropup {
          .dropdown-menu {
            transform: translate3d(-50%, calc(-100% - ($productshoppingcartbutton-padding-y + $productshoppingcartbutton-quantity-menu-caret-size + 2px)), 0)!important;

            &:after {
              display: block;
              content: '';
              position: absolute;
              top: 100%;
              left: 50%;
              width: 0;
              height: 0;
              border-width: $productshoppingcartbutton-quantity-menu-caret-size $productshoppingcartbutton-quantity-menu-caret-size 0;
              border-style: solid solid none;
              border-color: $productshoppingcartbutton-quantity-menu-bg transparent transparent;
              transform: translate3d(-50%, 0, 0);
            }
          }
        }

        &:not(.dropup) {
          .dropdown-menu {
            top: calc(100% + #{$productshoppingcartbutton-padding-y} + #{$productshoppingcartbutton-quantity-menu-caret-size} + 2px)!important;
            bottom: auto!important;
            transform: translate3d(-50%, 0, 0)!important;

            &:before {
              display: block;
              content: '';
              position: absolute;
              bottom: 100%;
              left: 50%;
              width: 0;
              height: 0;
              border-width: 0 $productshoppingcartbutton-quantity-menu-caret-size $productshoppingcartbutton-quantity-menu-caret-size;
              border-style: none solid solid;
              border-color: transparent transparent $productshoppingcartbutton-quantity-menu-bg;
              transform: translate3d(-50%, 0, 0);
            }
          }
        }
      }

      .form-control {
        padding: $productshoppingcartbutton-quantity-control-padding-y $productshoppingcartbutton-quantity-control-padding-x;
        height: 100%;
        background-color: $productshoppingcartbutton-quantity-control-bg;
        border-color: $productshoppingcartbutton-quantity-control-border;
        border-radius: $dropdown-border-radius;
        color: color-yiq($productshoppingcartbutton-quantity-control-bg);
        text-align: center;
        transition: $transition-base;

        &:focus {
          background-color: $productshoppingcartbutton-quantity-control-focus-bg!important;
          border-color: $productshoppingcartbutton-quantity-control-focus-border!important;
        }
      }
    }

    .shoppingcartbutton-text {
      padding: $productshoppingcartbutton-text-padding-y $productshoppingcartbutton-text-padding-x;
    }

    &:hover {
      .shoppingcartbutton-quantity {
        .dropdown {
          .dropdown-toggle {
            color: color-yiq($productshoppingcartbutton-quantity-toggle-hover-bg);
            @include gradient-bg($productshoppingcartbutton-quantity-toggle-hover-bg);
            border-color: $productshoppingcartbutton-quantity-toggle-hover-border;
          }
        }

        .form-control {
          background-color: $productshoppingcartbutton-quantity-control-hover-bg;
          border-color: $productshoppingcartbutton-quantity-control-hover-bg;
        }
      }
    }
  }

  &.small{
    .shoppingcartbutton{
      .shoppingcartbutton-quantity{
        height: auto;
        width: auto;
        .dropdown {
          .dropdown-toggle{
            padding: 0 1.2rem 0 0.8rem;
            background-position: center right;
          }
        }
        .form-control {
          width: $productshoppingcartbutton-quantity-width;
          min-width: $productshoppingcartbutton-quantity-width;
        }
      }
      .shoppingcartbutton-text{
        padding: 0 0.5rem;
      }
    }
  }
}
</style>
